/**
 * localStorage availability helpers.
 *
 * Some Android WebView environments expose `window.localStorage` as `null`
 * instead of `undefined`, and some privacy contexts throw on access.
 * Centralizing the guard prevents repeated try/catch logic across storage code.
 */
export function getLocalStorageIfAvailable(): Storage | null {
    if (typeof window === 'undefined') return null;

    try {
        return window.localStorage ?? null;
    } catch {
        return null;
    }
}

/**
 * Checks whether localStorage can be read and written.
 * This catches environments where the object exists but operations throw.
 */
export function isLocalStorageUsable(): boolean {
    const storage = getLocalStorageIfAvailable();
    if (!storage) return false;

    try {
        const probeKey = '__rose_storage_probe__';
        storage.setItem(probeKey, '1');
        storage.removeItem(probeKey);
        return true;
    } catch {
        return false;
    }
}
